import {
  Breadcrumb,
  Button,
  Card,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BankAccountCreateModal from "./components/BankAccountCreateModal";
import BankAccountEditModal from "./components/BankAccountEditModal";
import { getInstitution } from "../../../api/institutionApi";
import {
  getAccountsForAdmin,
} from "../../../api/bankAccountApi";
import { getBankLabel } from "./components/bankAccountConstants";

const { Title, Text } = Typography;

export default function InstitutionManageAccountsPage() {
  const { institutionId } = useParams();

  const [institution, setInstitution] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bankFilter, setBankFilter] = useState(undefined);
  const [currencyFilter, setCurrencyFilter] = useState(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const loadPage = async () => {
    try {
      setLoading(true);

      const [institutionData, accountData] = await Promise.all([
        getInstitution(institutionId),
        getAccountsForAdmin(institutionId),
      ]);

      setInstitution(institutionData || null);
      setAccounts(Array.isArray(accountData) ? accountData : []);
    } catch (error) {
      message.error("Мэдээлэл ачааллахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!institutionId) return;
    loadPage();
  }, [institutionId]);

  const bankOptions = useMemo(() => {
    const unique = [...new Set(accounts.map((item) => item.bank).filter(Boolean))];
    return unique.map((item) => ({
      label: getBankLabel(item),
      value: item,
    }));
  }, [accounts]);

  const currencyOptions = useMemo(() => {
    const unique = [
      ...new Set(
        accounts
          .map((item) => item?.currency?.code || item?.currencyCode || item?.currency)
          .filter(Boolean)
      ),
    ];

    return unique.map((item) => ({
      label: item,
      value: item,
    }));
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    return accounts.filter((item) => {
      const bankMatch = !bankFilter || item.bank === bankFilter;

      const currencyValue =
        item?.currency?.code || item?.currencyCode || item?.currency || "";
      const currencyMatch = !currencyFilter || currencyValue === currencyFilter;

      return bankMatch && currencyMatch;
    });
  }, [accounts, bankFilter, currencyFilter]);

  const openEditModal = (record) => {
    setSelectedAccount(record);
    setEditOpen(true);
  };

  const columns = [
    {
      title: "Банк",
      dataIndex: "bank",
      key: "bank",
      render: (value) => <span>{getBankLabel(value)}</span>,
    },
    {
      title: "Дансны дугаар",
      dataIndex: "accountNumber",
      key: "accountNumber",
      render: (value) => (
        <span className="font-mono text-slate-700">{value || "-"}</span>
      ),
    },
    {
      title: "Валют",
      key: "currency",
      width: 120,
      render: (_, record) => {
        const code =
          record?.currency?.code || record?.currencyCode || record?.currency || "-";
        return <Tag color="blue">{code}</Tag>;
      },
    },
    {
      title: "Төлөв",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (_, record) =>
        record?.is_active || record?.isActive ? (
          <Tag color="green">Идэвхтэй</Tag>
        ) : (
          <Tag>Идэвхгүй</Tag>
        ),
    },
    {
      title: "Үйлдэл",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => openEditModal(record)}
        >
          Засах
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-5">
      <Card className="rounded-2xl shadow-sm">
        <div className="flex flex-col gap-4">
          <Breadcrumb
            items={[
              {
                title: <Link to="/admin/institutions">Байгуулгууд</Link>,
              },
              {
                title: institution?.name || "Дэлгэрэнгүй",
              },
            ]}
          />

          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <Title level={3} className="!mb-1">
                {institution?.name || "Байгууллага"}
              </Title>
              <Text className="text-slate-500">
                Байгууллагын дансны жагсаалт болон шинээр данс үүсгэх хэсэг
              </Text>
            </div>

            <Space wrap>
              <Select
                allowClear
                value={bankFilter}
                onChange={setBankFilter}
                options={bankOptions}
                placeholder="Банкаар шүүх"
                style={{ width: 220 }}
              />
              <Select
                allowClear
                value={currencyFilter}
                onChange={setCurrencyFilter}
                options={currencyOptions}
                placeholder="Валютаар шүүх"
                style={{ width: 180 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateOpen(true)}
              >
                Данс нэмэх
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl shadow-sm">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredAccounts}
          pagination={{ pageSize: 100, showSizeChanger: false }}
          locale={{
            emptyText: <Empty description="Данс олдсонгүй" />,
          }}
        />
      </Card>

      <BankAccountCreateModal
        open={createOpen}
        institutionId={Number(institutionId)}
        institutionName={institution?.name}
        onClose={() => setCreateOpen(false)}
        onSuccess={loadPage}
      />

      <BankAccountEditModal
        open={editOpen}
        account={selectedAccount}
        institutionName={institution?.name}
        onClose={() => {
          setEditOpen(false);
          setSelectedAccount(null);
        }}
        onSuccess={loadPage}
      />
    </div>
  );
}